import { Link, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { useAdminAuth } from "../../lib/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LOGO_URL } from "../../lib/site";
import RateSettings from "./sections/RateSettings";
import BlogManager from "./sections/BlogManager";
import UsersManager from "./sections/UsersManager";
import ContactsManager from "./sections/ContactsManager";
import TestimonialsManager from "./sections/TestimonialsManager";
import CustomersManager from "./sections/CustomersManager";
import TransactionsManager from "./sections/TransactionsManager";
import WithdrawalsManager from "./sections/WithdrawalsManager";

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="CashPark" className="h-8 w-8" />
            <span className="font-display font-bold text-[#0B192C]">CashPark <span className="text-[#FF6B00]">Admin</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="text-xs text-slate-500 hover:text-[#FF6B00] inline-flex items-center gap-1" data-testid="admin-view-site">
              View site <ExternalLink size={12} />
            </Link>
            <span className="text-xs text-slate-500 hidden sm:inline" data-testid="admin-email-display">{admin?.email}</span>
            <Button variant="outline" onClick={onLogout} className="rounded-full h-9 px-4 text-sm" data-testid="admin-logout-button">
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl font-bold text-[#0B192C]">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Manage rates, content, users, and inquiries.</p>

        <Tabs defaultValue="rates" className="mt-8">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-full h-auto flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="rates" data-testid="tab-rates" className="rounded-full data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-4 py-2">Rate Settings</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-full data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-4 py-2">Customers</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions" className="rounded-full data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-4 py-2">Transactions</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="tab-withdrawals" className="rounded-full data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-4 py-2">Withdrawals</TabsTrigger>
            <TabsTrigger value="blogs" data-testid="tab-blogs" className="rounded-full data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-4 py-2">Blog Posts</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="rounded-full data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-4 py-2">Testimonials</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-full data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-4 py-2">Dojah KYC</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="rounded-full data-[state=active]:bg-[#0B192C] data-[state=active]:text-white px-4 py-2">Contact Inbox</TabsTrigger>
          </TabsList>

          <TabsContent value="rates" className="mt-6"><RateSettings /></TabsContent>
          <TabsContent value="customers" className="mt-6"><CustomersManager /></TabsContent>
          <TabsContent value="transactions" className="mt-6"><TransactionsManager /></TabsContent>
          <TabsContent value="withdrawals" className="mt-6"><WithdrawalsManager /></TabsContent>
          <TabsContent value="blogs" className="mt-6"><BlogManager /></TabsContent>
          <TabsContent value="testimonials" className="mt-6"><TestimonialsManager /></TabsContent>
          <TabsContent value="users" className="mt-6"><UsersManager /></TabsContent>
          <TabsContent value="contacts" className="mt-6"><ContactsManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
